// client/src/components/PublicProfile/PublicLeaderboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, Crown, Star, Target, TrendingUp, Users, Zap,
  ArrowLeft, RefreshCw, Search, Filter, Medal, Award, Globe
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import PublicProfileSearch from './PublicProfileSearch';

// Import enhanced translation system
import { translations, languageConfig, getTranslation, isRTL, getAvailableLanguages } from '../../translations/translations';

const PublicLeaderboard = ({ user, currentLanguage: propCurrentLanguage, t: propT, onLanguageChange }) => {
  const navigate = useNavigate();

  // Use propCurrentLanguage directly - App.js is the source of truth
  // Only fallback to localStorage if no prop provided (standalone mode)
  const currentLanguage = propCurrentLanguage || localStorage.getItem('preferred-language') || 'en';

  // State for language dropdown
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const availableLanguages = getAvailableLanguages();

  // Language change handler that syncs with parent component
  const handleLanguageSelect = (langCode) => {
    localStorage.setItem('preferred-language', langCode);
    setShowLangDropdown(false);
    // Sync with parent - this is the primary way to change language
    if (onLanguageChange) {
      onLanguageChange(langCode);
    }
  };

  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leaderboardType, setLeaderboardType] = useState('level');
  const [refreshing, setRefreshing] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Enhanced translation function with fallback
  const t = React.useCallback((key, fallback = null) => {
    // Use prop function if available
    if (propT) return propT(key, fallback);
    // Otherwise use our own translation function
    return getTranslation(key, currentLanguage, fallback);
  }, [propT, currentLanguage]);

  // Critical: Apply RTL layout on component mount and language change
  useEffect(() => {
    const isRtl = isRTL(currentLanguage);
    const htmlElement = document.documentElement;
    
    // Set direction and language attributes
    htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    htmlElement.setAttribute('lang', currentLanguage);
    
    // Apply or remove RTL class
    if (isRtl) {
      htmlElement.classList.add('rtl-layout');
    } else {
      htmlElement.classList.remove('rtl-layout');
    }
    
    console.log('🌐 PublicLeaderboard RTL setup:', {
      language: currentLanguage,
      isRtl,
      direction: htmlElement.getAttribute('dir'),
      hasRtlClass: htmlElement.classList.contains('rtl-layout')
    });
    
    // Language preference is saved in handleLanguageToggle
  }, [currentLanguage]);

  useEffect(() => {
    loadLeaderboard(leaderboardType);
  }, [leaderboardType]);

  const loadLeaderboard = async (type = 'level') => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`/api/public-profile/leaderboard/top?type=${type}&limit=100`);
      setLeaderboardData(response.data);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
      setError(error.response?.data?.message || t('failedToLoadLeaderboard', 'Failed to load leaderboard'));
      toast.error(t('failedToLoadLeaderboard', 'Failed to load leaderboard'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadLeaderboard(leaderboardType);
    setRefreshing(false);
    toast.success(t('leaderboardRefreshed', 'Leaderboard refreshed!'));
  };

  const handlePlayerClick = (player) => {
    // Mark that we're coming from leaderboard for smart back navigation
    sessionStorage.setItem('profileReferrer', 'leaderboard');
    navigate(`/profile/${player.username}`);
  };

  const handleSearch = (searchIdentifier) => {
    // Mark that we're coming from leaderboard search for smart back navigation
    sessionStorage.setItem('profileReferrer', 'leaderboard');
    navigate(`/profile/${searchIdentifier}`);
  };

  const handleBack = () => {
    // If search modal is open, close it first
    if (showSearch) {
      setShowSearch(false);
      return;
    }
    
    // From leaderboard, always go back to main website
    navigate('/');
  };

  const getLeaderboardTitle = (type) => {
    const titles = {
      'level': t('levelLeaderboard', 'Level Leaderboard'),
      'wins': t('winsLeaderboard', 'Wins Leaderboard'),
      'winstreak': t('winStreakLeaderboard', 'Win Streak Leaderboard'),
      'points': t('pointsLeaderboard', 'Points Leaderboard')
    };
    return titles[type] || titles['level'];
  };

  const getLeaderboardDescription = (type) => {
    const descriptions = {
      'level': t('levelLeaderboardDesc', 'Top soldiers ranked by experience'),
      'wins': t('winsLeaderboardDesc', 'Most victorious commanders'),
      'winstreak': t('winStreakLeaderboardDesc', 'Current combat streaks'),
      'points': t('pointsLeaderboardDesc', 'Highest battle earnings')
    };
    return descriptions[type] || descriptions['level'];
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-400" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-300" />;
      case 3:
        return <Award className="w-5 h-5 text-yellow-600" />;
      default:
        return <span className="rank-number">#{rank}</span>;
    }
  };

  const getRankClass = (rank) => {
    switch (rank) {
      case 1:
        return 'rank-1';
      case 2:
        return 'rank-2';
      case 3:
        return 'rank-3';
      default:
        return rank <= 10 ? 'rank-top-10' : 'rank-default';
    }
  };

  const formatDisplayValue = (player, type) => {
    switch (type) {
      case 'level':
        return {
          primary: `${t('level', 'Level')} ${player.currentLevel}`,
          secondary: `${player.totalExp?.toLocaleString() || 0} ${t('exp', 'EXP')}`
        };
      case 'wins':
        return {
          primary: `${player.wins} ${t('wins', 'Wins')}`,
          secondary: `${player.winRate}% ${t('winRate', 'Win Rate')}`
        };
      case 'winstreak':
        return {
          primary: `${player.currentWinStreak} ${t('currentStreak', 'Current Streak')}`,
          secondary: `${t('maxStreak', 'Max')}: ${player.maxWinStreak}`
        };
      case 'points':
        return {
          primary: `${player.points?.toLocaleString() || 0} ${t('points', 'Points')}`,
          secondary: `${player.wins} ${t('wins', 'Wins')}`
        };
      default:
        return {
          primary: `${t('level', 'Level')} ${player.currentLevel}`,
          secondary: `${player.totalExp?.toLocaleString() || 0} ${t('exp', 'EXP')}`
        };
    }
  };

  const leaderboardTypes = [
    {
      id: 'level',
      label: t('level', 'Level'),
      icon: Zap,
      description: t('levelDesc', 'Experience'),
      color: 'var(--cod-yellow)'
    },
    {
      id: 'wins',
      label: t('wins', 'Wins'),
      icon: Trophy,
      description: t('winsDesc', 'Victories'),
      color: 'var(--cod-green)'
    },
    {
      id: 'winstreak',
      label: t('winStreak', 'Streaks'),
      icon: TrendingUp,
      description: t('winStreakDesc', 'Current'),
      color: 'var(--cod-red)'
    },
    {
      id: 'points',
      label: t('points', 'Points'),
      icon: Target,
      description: t('pointsDesc', 'Earnings'),
      color: 'var(--cod-orange)'
    }
  ];

  if (loading) {
    return (
      <div className="public-profile-container">
        {/* Floating Action Buttons - Mobile */}
        <div className="floating-actions">
          <div className="floating-lang-wrapper">
            <button
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              className="floating-btn lang-btn"
              title={t('language', 'Language')}
            >
              {languageConfig[currentLanguage]?.flag || '🌐'}
            </button>
            {showLangDropdown && (
              <div className="floating-lang-dropdown">
                {availableLanguages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                  >
                    <span className="lang-flag">{lang.flag}</span>
                    <span className="lang-name">{lang.nativeName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleBack}
            className="floating-btn back-btn"
            title={t('back', 'Back')}
          >
            <ArrowLeft />
          </button>
        </div>

        <div className="public-profile-header">
          <div className="header-top">
            <div className="header-info">
              <h1 className="public-profile-title">{t('loadingLeaderboard', 'Loading Leaderboard...')}</h1>
            </div>
            <div className="header-actions">
              <button onClick={handleBack} className="back-button">
                <ArrowLeft className="w-4 h-4" />
                {t('back', 'Back')}
              </button>
              <div className="language-dropdown-wrapper">
                <button
                  onClick={() => setShowLangDropdown(!showLangDropdown)}
                  className="language-button"
                  title={t('language', 'Language')}
                >
                  <Globe className="w-4 h-4" />
                  {languageConfig[currentLanguage]?.flag} {languageConfig[currentLanguage]?.nativeName}
                </button>
                {showLangDropdown && (
                  <div className="language-dropdown">
                    {availableLanguages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageSelect(lang.code)}
                        className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                      >
                        <span className="lang-flag">{lang.flag}</span>
                        <span className="lang-name">{lang.nativeName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="public-profile-content">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{t('loadingRankings', 'Loading player rankings...')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-profile-container">
        {/* Floating Action Buttons - Mobile */}
        <div className="floating-actions">
          <div className="floating-lang-wrapper">
            <button
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              className="floating-btn lang-btn"
              title={t('language', 'Language')}
            >
              {languageConfig[currentLanguage]?.flag || '🌐'}
            </button>
            {showLangDropdown && (
              <div className="floating-lang-dropdown">
                {availableLanguages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                  >
                    <span className="lang-flag">{lang.flag}</span>
                    <span className="lang-name">{lang.nativeName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleBack}
            className="floating-btn back-btn"
            title={t('back', 'Back')}
          >
            <ArrowLeft />
          </button>
        </div>

        <div className="public-profile-header">
          <div className="header-top">
            <div className="header-info">
              <h1 className="public-profile-title">{t('leaderboard', 'Leaderboard')}</h1>
            </div>
            <div className="header-actions">
              <button onClick={handleBack} className="back-button">
                <ArrowLeft className="w-4 h-4" />
                {t('back', 'Back')}
              </button>
              <div className="language-dropdown-wrapper">
                <button
                  onClick={() => setShowLangDropdown(!showLangDropdown)}
                  className="language-button"
                  title={t('language', 'Language')}
                >
                  <Globe className="w-4 h-4" />
                  {languageConfig[currentLanguage]?.flag} {languageConfig[currentLanguage]?.nativeName}
                </button>
                {showLangDropdown && (
                  <div className="language-dropdown">
                    {availableLanguages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageSelect(lang.code)}
                        className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                      >
                        <span className="lang-flag">{lang.flag}</span>
                        <span className="lang-name">{lang.nativeName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="public-profile-content">
          <div className="error-state">
            <Trophy className="w-12 h-12 text-red-400 mb-4" />
            <h3 className="text-lg font-semibold text-red-400 mb-2">{error}</h3>
            <p className="text-gray-400 mb-4">
              {t('leaderboardLoadError', 'There was an error loading the leaderboard. Please try again.')}
            </p>
            <button 
              onClick={() => loadLeaderboard(leaderboardType)}
              className="retry-button"
            >
              {t('retry', 'Retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile-container">
      {/* Floating Action Buttons - Mobile */}
      <div className="floating-actions">
        <div className="floating-lang-wrapper">
          <button
            onClick={() => setShowLangDropdown(!showLangDropdown)}
            className="floating-btn lang-btn"
            title={t('language', 'Language')}
          >
            {languageConfig[currentLanguage]?.flag || '🌐'}
          </button>
          {showLangDropdown && (
            <div className="floating-lang-dropdown">
              {availableLanguages.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                >
                  <span className="lang-flag">{lang.flag}</span>
                  <span className="lang-name">{lang.nativeName}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowSearch(true)}
          className="floating-btn search-btn"
          title={t('search', 'Search')}
        >
          <Search />
        </button>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="floating-btn refresh-btn"
          title={t('refresh', 'Refresh')}
        >
          <RefreshCw className={refreshing ? 'animate-spin' : ''} />
        </button>
        <button
          onClick={handleBack}
          className="floating-btn back-btn"
          title={t('back', 'Back')}
        >
          <ArrowLeft />
        </button>
      </div>

      {/* Header */}
      <div className="public-profile-header">
        <div className="header-top">
          <div className="header-info">
            <h1 className="public-profile-title">
              {getLeaderboardTitle(leaderboardType)}
            </h1>
            <p className="leaderboard-description">
              {getLeaderboardDescription(leaderboardType)}
            </p>
            {leaderboardData.generatedAt && (
              <div className="leaderboard-meta">
                {t('lastUpdated', 'Last updated')}: {new Date(leaderboardData.generatedAt).toLocaleString()}
              </div>
            )}
          </div>
          <div className="header-actions">
            <button onClick={handleBack} className="back-button">
              <ArrowLeft className="w-4 h-4" />
              {t('back', 'Back')}
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('refresh', 'Refresh')}
            </button>
            <button
              onClick={() => setShowSearch(true)}
              className="search-button"
            >
              <Search className="w-4 h-4" />
              {t('search', 'Search')}
            </button>
            <div className="language-dropdown-wrapper">
              <button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="language-button"
                title={t('language', 'Language')}
              >
                <Globe className="w-4 h-4" />
                {languageConfig[currentLanguage]?.flag} {languageConfig[currentLanguage]?.nativeName}
              </button>
              {showLangDropdown && (
                <div className="language-dropdown">
                  {availableLanguages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageSelect(lang.code)}
                      className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                    >
                      <span className="lang-flag">{lang.flag}</span>
                      <span className="lang-name">{lang.nativeName}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Type Selector */}
      <div className="leaderboard-types">
        {leaderboardTypes.map(type => {
          const Icon = type.icon;
          return (
            <button
              key={type.id}
              onClick={() => setLeaderboardType(type.id)}
              className={`type-button ${leaderboardType === type.id ? 'active' : ''}`}
              style={{
                '--type-color': type.color
              }}
            >
              <Icon className="w-5 h-5" style={{ color: type.color }} />
              <div className="type-info">
                <span className="type-label">{type.label}</span>
                <span className="type-description">{type.description}</span>
              </div>
              {leaderboardType === type.id && (
                <div className="type-indicator" style={{ backgroundColor: type.color }}></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Leaderboard Content */}
      <div className="public-profile-content">
        {leaderboardData.leaderboard && leaderboardData.leaderboard.length === 0 ? (
          <div className="empty-state">
            <Trophy className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">
              {t('noDataAvailable', 'No Data Available')}
            </h3>
            <p className="text-gray-400">
              {leaderboardType === 'winstreak' 
                ? t('noActiveStreaks', 'No soldiers currently have active win streaks.')
                : t('noPlayersFound', 'No soldiers found for this leaderboard.')
              }
            </p>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {leaderboardData.leaderboard && leaderboardData.leaderboard.length >= 3 && (
              <div className="podium-section">
                <h3 className="podium-title">
                  <Crown className="w-5 h-5" />
                  {t('topCommanders', 'Top Commanders')}
                </h3>
                <div className="podium">
                  {/* 2nd Place */}
                  <div className="podium-position second" onClick={() => handlePlayerClick(leaderboardData.leaderboard[1])}>
                    <div className="podium-rank">
                      <Medal className="w-6 h-6 text-gray-300" />
                    </div>
                    <div className="podium-player">
                      <div className="player-avatar">
                        <Users className="w-6 h-6" />
                        {leaderboardData.leaderboard[1].isOnline && <div className="status-dot online"></div>}
                      </div>
                      <div className="player-info">
                        <div className="player-name">{leaderboardData.leaderboard[1].inGameName}</div>
                        <div className="player-value">
                          {formatDisplayValue(leaderboardData.leaderboard[1], leaderboardType).primary}
                        </div>
                        <div className="rank-badge silver">#{2}</div>
                      </div>
                    </div>
                  </div>

                  {/* 1st Place */}
                  <div className="podium-position first" onClick={() => handlePlayerClick(leaderboardData.leaderboard[0])}>
                    <div className="podium-rank">
                      <Crown className="w-8 h-8 text-yellow-400" />
                    </div>
                    <div className="podium-player">
                      <div className="player-avatar champion">
                        <Users className="w-8 h-8" />
                        {leaderboardData.leaderboard[0].isOnline && <div className="status-dot online"></div>}
                      </div>
                      <div className="player-info">
                        <div className="player-name">{leaderboardData.leaderboard[0].inGameName}</div>
                        <div className="player-value">
                          {formatDisplayValue(leaderboardData.leaderboard[0], leaderboardType).primary}
                        </div>
                        <div className="champion-badge">{t('champion', 'Champion')}</div>
                      </div>
                    </div>
                  </div>

                  {/* 3rd Place */}
                  <div className="podium-position third" onClick={() => handlePlayerClick(leaderboardData.leaderboard[2])}>
                    <div className="podium-rank">
                      <Award className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div className="podium-player">
                      <div className="player-avatar">
                        <Users className="w-6 h-6" />
                        {leaderboardData.leaderboard[2].isOnline && <div className="status-dot online"></div>}
                      </div>
                      <div className="player-info">
                        <div className="player-name">{leaderboardData.leaderboard[2].inGameName}</div>
                        <div className="player-value">
                          {formatDisplayValue(leaderboardData.leaderboard[2], leaderboardType).primary}
                        </div>
                        <div className="rank-badge bronze">#{3}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Full Leaderboard List */}
            <div className="leaderboard-list">
              <div className="list-header">
                <h3 className="list-title">
                  <Filter className="w-4 h-4" />
                  {t('fullRankings', 'All Soldiers')}
                  <span className="list-count">({leaderboardData.totalCount} {t('active', 'active')})</span>
                </h3>
              </div>
              
              <div className="ranking-list">
                {leaderboardData.leaderboard?.map((player, index) => {
                  const displayValues = formatDisplayValue(player, leaderboardType);
                  const isCurrentUser = user && user._id === player._id;
                  
                  return (
                    <div
                      key={player._id}
                      onClick={() => handlePlayerClick(player)}
                      className={`ranking-item ${getRankClass(player.rank)} ${isCurrentUser ? 'current-user' : ''}`}
                    >
                      <div className="ranking-rank">
                        {getRankIcon(player.rank)}
                      </div>
                      
                      <div className="ranking-player">
                        <div className="player-avatar">
                          <Users className="w-6 h-6" />
                          {player.isOnline && <div className="status-dot online"></div>}
                        </div>
                        
                        <div className="player-details">
                          <div className="player-name">
                            {player.inGameName}
                            {isCurrentUser && (
                              <span className="you-badge">{t('you', '(You)')}</span>
                            )}
                          </div>
                          <div className="player-username">@{player.username}</div>
                          <div className="player-meta">
                            {t('deployed', 'Deployed')}: {player.accountAge} {t('days', 'days')}
                          </div>
                        </div>
                      </div>
                      
                      <div className="ranking-stats">
                        <div className="primary-stat">
                          {displayValues.primary}
                        </div>
                        <div className="secondary-stat">
                          {displayValues.secondary}
                        </div>
                      </div>
                      
                      <div className="ranking-indicator">
                        <div className={`status-text ${player.isOnline ? 'online' : 'offline'}`}>
                          {player.isOnline ? t('active', 'Active') : t('offline', 'Offline')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Search Modal */}
      {showSearch && (
        <div className="search-modal-overlay" onClick={() => setShowSearch(false)}>
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-header">
              <h3>{t('searchForSoldier', 'Search for a Soldier')}</h3>
              <button 
                onClick={() => setShowSearch(false)}
                className="close-button"
              >
                ×
              </button>
            </div>
            <PublicProfileSearch 
              onSearch={(searchIdentifier) => {
                setShowSearch(false);
                handleSearch(searchIdentifier);
              }} 
              t={t} 
              placeholder={t('searchFromLeaderboard', 'Search any soldier...')}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicLeaderboard;